<script module>
  export const i18n = {
    sv: {
      'Price fr.': 'Pris fr.',
      'Buy Pass': 'Köp Pass',
      'No passes found': 'Inga pass hittades'
    },
    da: {
      'Price fr.': 'Pris fra',
      'Buy Pass': 'Køb Pass',
      'No passes found': 'Ingen pas fundet'
    }
  }
</script>

<script>
  import { WITHOUT_DECIMALS, money } from '$lib/utils/money.svelte.js'
  import { register } from '$lib/utils/text.svelte.js'
  import interrail from '$lib/assets/interrail.svg'
  import {
    EURAIL_PRODUCT_REGEX,
    getBundleTravelDays,
    getBundleValidity,
    i18n as builderI18n
  } from '$lib/PassBundleBuilder.svelte'
  import Html from '$lib/Html.svelte'

  const BRAND_LOGOTYPES = {
    eurail: interrail,
    interrail: interrail,
    'german-rail': interrail
  }

  let text = register(i18n, builderI18n)

  /** @type {{ bundles?: Schema.PassBundle[], badge?: string, loading?: boolean, onselect?: function(Schema.PassBundle): void, label?: Svelte.Snippet<[Schema.PassBundle]>, price?: Svelte.Snippet<[Schema.PassBundle]> }} */
  let {
    loading = false,
    bundles = [],
    badge,
    onselect,
    label,
    price
  } = $props()

  const getProductBrandName = (product) => {
    if (EURAIL_PRODUCT_REGEX.test(product)) {
      return product.match(EURAIL_PRODUCT_REGEX)[1]
    }
    return null
  }
</script>

<div class="pass-bundles">
  {#if !bundles?.length}
    {#if loading}
      <div class="items">
        {#each Array(4) as _, index}
          <div class="item loading" style:--delay="{index * 100}ms"></div>
        {/each}
      </div>
    {:else}
      <Html middle center>
        <p>{text`No passes found`}</p>
      </Html>
    {/if}
  {:else}
    <div class="items">
      {#each bundles as bundle, index}
        {@const brand = getProductBrandName(bundle.product)}
        {@const logotype = BRAND_LOGOTYPES[brand]}
        <article
          class="item {brand}"
          class:badged={badge}
          style:--delay="{Math.max(0, index - 4) * 100}ms">
          <div class="body">
            {#if logotype}
              <img class="logo" loading="lazy" alt={brand} src={logotype} />
            {/if}
            {#if badge}
              <span class="badge">
                {badge}
              </span>
            {/if}
            <h3 class="heading">
              {#if label}
                <span class="label">{@render label(bundle)}</span>
              {/if}
              <span class="validity">{getBundleTravelDays(bundle, text)}</span>
              <span class="subheading">{getBundleValidity(bundle, text)}</span>
            </h3>
            <p class="price">
              {text`Price fr.`}
              {#if price}
                {@render price(bundle)}
              {:else}
                <strong>{money(bundle.price, WITHOUT_DECIMALS)}</strong>
              {/if}
            </p>
            <button
              name="passBundleId"
              value={bundle.id}
              class="action"
              onclick={() => onselect?.(bundle)}>
              {text`Buy Pass`}
            </button>
          </div>
        </article>
      {/each}
    </div>
  {/if}
</div>

<style>
  .pass-bundles {
    container-type: inline-size;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
  }

  .variant {
    width: 100%;
    max-width: 20rem;
  }

  .items {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
    gap: 1.5rem;
    width: 100%;
  }

  @container (width >= 300px) and (width < 500px) {
    .items {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @container (width >= 1000px) {
    .items {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .item {
    --current-background: var(--background);
    --current-color: rgb(var(--rgb-text));

    container-type: inline-size;
    display: flex;
    width: 100%;
    position: relative;
    overflow: hidden;
    text-align: center;
    color: var(--current-color);
    border-radius: var(--border-radius-xl);
    background: var(--background-alt);
  }

  .item:not(.loading) {
    opacity: 0;
    transform: translateY(50%);
    animation: slide-in 500ms var(--delay, 0) forwards
      cubic-bezier(0.25, 1, 0.5, 1);
  }

  @keyframes slide-in {
    to {
      opacity: 1;
      transform: translateY(0%);
    }
  }

  .item:not(.loading)::after {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    z-index: -1;
    background: var(--current-background);
  }

  .item:not(.loading):nth-child(-n + 4) {
    opacity: 1;
    transform: none;
  }

  .item:not(.loading):nth-child(-n + 4)::after {
    opacity: 0;
    animation: fade-in 550ms var(--delay, 0) forwards
      cubic-bezier(0.25, 1, 0.5, 1);
  }

  @keyframes fade-in {
    to {
      opacity: 1;
    }
  }

  .item::before {
    content: '';
    display: block;
    padding-top: 150%;
  }

  .body {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: clamp(0.75rem, 3cqw, 1rem);
    width: 100%;
    padding: 1rem;
    opacity: 0;
    animation: fade-in 550ms var(--delay, 0) forwards
      cubic-bezier(0.25, 1, 0.5, 1);
  }

  .item.badged .body {
    padding-top: 3rem;
  }

  @media (min-width: 500px) {
    .body {
      padding: 2.5rem 1.25rem 1.25rem;
    }
  }

  .interrail,
  .eurail,
  .german-rail {
    --rgb-highlight: var(--rgb-white);
    --current-background: linear-gradient(
      347.57deg,
      #1c0744 10.5%,
      #2e0e6a 80.75%
    );
    --current-color: #fff;
  }

  /** Badge */

  .badge {
    padding: 0.1rem 5em;
    position: absolute;
    top: 0;
    left: 0;
    font-weight: bold;
    font-size: clamp(0.875rem, 10cqw, 1.75rem);
    white-space: nowrap;
    background: rgb(var(--rgb-primary, var(--rgb-red)));
    color: rgb(var(--rgb-white));
    transform-origin: 50% 0;
    transform: translateX(-50%) rotate(-45deg) translateY(90%);
  }

  .logo {
    width: 8rem;
    width: max(45%, 10cqw);
  }

  @container (width < 170px) {
    .logo {
      display: none;
    }
  }

  .item.is-loading .logo::after {
    content: '';
    display: block;
    padding-top: 100%;
  }

  .heading {
    margin: auto 0;
    font-size: clamp(2rem, 15cqw, 3rem);
    white-space: nowrap;
    font-weight: bold;
    line-height: 1;
  }

  .validity {
    font-family: var(--heading-font-family);
  }

  .subheading {
    display: block;
    margin-top: 0.25em;
    font-weight: normal;
    font-size: 1.375rem;
    font-size: clamp(1rem, 7cqw, 1.25rem);
  }

  .price {
    display: flex;
    gap: 0.5em;
    flex-wrap: wrap;
    justify-content: center;
    line-height: 1.1;
    font-size: clamp(1rem, 7cqw, 1.25rem);
  }

  .action {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: clamp(0.5rem, 6cqw, 1.125rem) 0;
    font-size: 1rem;
    font-weight: bold;
    white-space: nowrap;
    color: rgb(var(--rgb-text));
    background-color: rgb(var(--rgb-highlight));
    border-radius: var(--border-radius-full);
    box-shadow: var(--box-shadow-sm);
    transition: var(--box-shadow-transition);
    cursor: pointer;
    user-select: none;
  }

  .action:hover {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .action:active {
    box-shadow: var(--box-shadow-sm-active);
  }

  .interrail .action,
  .eurail .action,
  .german-rail .action {
    --current-color: #2e0e6a;
  }

  .label:not(:empty) {
    display: block;
    font-size: 1.125rem;
    font-weight: bold;
    margin-bottom: 0.5rem;
  }
</style>
